import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { authService } from '../services/api'
import { useWishlistStore } from '../store/wishlistStore'
import { useCartStore } from '../store/cartStore'
import { useQueryClient } from '@tanstack/react-query'

const AuthContext = createContext()

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [customer, setCustomer] = useState(null)
  const [loading, setLoading] = useState(true)
  const [token, setToken] = useState(() => localStorage.getItem('tonys-token'))
  const syncWishlist = useWishlistStore(s => s.syncFromServer)
  const clearWishlist = useWishlistStore(s => s.clear)
  const claimCart = useCartStore(s => s.claimCart)
  const queryClient = useQueryClient()

  const loadUser = useCallback(async () => {
    if (!token) { setLoading(false); return }
    try {
      const data = await authService.me()
      setUser(data)
      if (data.role !== 'admin') {
        try {
          const cust = await authService.customerMe()
          setCustomer(cust)
        } catch {
          setCustomer(null)
        }
      }
      syncWishlist()
    } catch {
      localStorage.removeItem('tonys-token')
      setToken(null)
    } finally {
      setLoading(false)
    }
  }, [token]) // eslint-disable-line

  useEffect(() => { loadUser() }, [loadUser])

  const login = async (username, password) => {
    const data = await authService.login(username, password)
    // If 2FA is required, return signal — caller shows TOTP or email step
    if (data.requires_2fa) return {
      requires2fa: true,
      twoFaMethod: data.two_fa_method || 'totp',
      emailHint: data.email_hint || '',
    }
    localStorage.setItem('tonys-token', data.access_token)
    setToken(data.access_token)
    const userData = await authService.me()
    setUser(userData)
    let cust = null
    if (userData.role !== 'admin') {
      try {
        cust = await authService.customerMe()
        setCustomer(cust)
      } catch {
        setCustomer(null)
      }
    }
    queryClient.invalidateQueries()
    claimCart(userData.id)
    syncWishlist()
    return { requires2fa: false, user: userData }
  }

  const loginWithToken = async (access_token) => {
    localStorage.setItem('tonys-token', access_token)
    setToken(access_token)
    const userData = await authService.me()
    setUser(userData)
    let cust = null
    if (userData.role !== 'admin') {
      try {
        cust = await authService.customerMe()
        setCustomer(cust)
      } catch {
        setCustomer(null)
      }
    }
    queryClient.invalidateQueries()
    claimCart(userData.id)
    syncWishlist()
    return userData
  }

  const logout = () => {
    localStorage.removeItem('tonys-token')
    setToken(null)
    setUser(null)
    setCustomer(null)
    clearWishlist()
    // Clear all cached product data so next login sees correct products
    queryClient.clear()
  }

  const register = async (userData) => {
    const newUser = await authService.register(userData)
    return newUser
  }

  const canViewAlcohol = customer ? !!customer.list_liquor : true
  const priceTier = customer?.price_list_tier ?? 2

  return (
    <AuthContext.Provider value={{ user, customer, loading, token, login, loginWithToken, logout, register, isAuthenticated: !!user, canViewAlcohol, priceTier }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
